import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { adminKeys } from './useAdmin';

/**
 * Per-org trainee "experience template". A superadmin picks, per organization,
 * which trainee-facing UI its members see:
 *   - 'default'  — the current SaaS dashboard
 *   - 'fortress' — the gamified operator-console experience
 *
 * The value lives in the org's settings (server-side) and is read here so the
 * app can pick which shell to render. Readable by any authenticated user
 * (trainees included) via GET /me/experience.
 */
export type ExperienceTemplate = 'default' | 'fortress';

interface ExperienceResponse {
  template: ExperienceTemplate;
}

export const experienceKeys = {
  me: () => ['experience', 'me'] as const,
};

/** The caller's own org experience template. Never throws the app down —
 * falls back to 'default' on any error so the standard UI always renders. */
export function useMyExperience() {
  return useQuery<ExperienceTemplate>({
    queryKey: experienceKeys.me(),
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      try {
        const resp = await apiFetch<ExperienceResponse>('/me/experience', { method: 'GET', signal });
        return resp?.template === 'fortress' ? 'fortress' : 'default';
      } catch {
        return 'default';
      }
    },
  });
}

/** Superadmin: set an org's trainee experience template. */
export function useSetOrgTemplate() {
  const qc = useQueryClient();
  return useMutation<{ id: string; template: ExperienceTemplate }, Error, { tenantId: string; template: ExperienceTemplate }>({
    mutationFn: async ({ tenantId, template }) =>
      apiFetch('/tenants/{tenantId}/template', {
        method: 'POST',
        pathParams: { tenantId },
        body: { template },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.tenants() });
      qc.invalidateQueries({ queryKey: experienceKeys.me() });
    },
  });
}
